import type { ManipulateType } from 'dayjs';
import type { TFunction } from 'i18next';

import { dayjs } from '../dayjs.utils';

export type DurationType = {
  storageKey?: string; // Local Storage Key to store the selected duration for the widget e.g. 'AWN_LOGIN_EVENTS_DURATION'
  passDurationToDetailsView?: boolean;
};

export enum DurationValue {
  OneHour = '1h',
  TwelveHour = '12h',
  TwentyFourHour = '24h',
  SeventyTwoHour = '72h',
  SevenDay = '7d',
  TenDay = '10d',
}

export type DurationOption = {
  value: DurationValue;
  label: string;
  zendeskValue?: string;
};

export const DEFAULT_DURATION = DurationValue.SeventyTwoHour;
export const ALL_DURATION_OPTIONS = [
  DurationValue.OneHour,
  DurationValue.TwelveHour,
  DurationValue.TwentyFourHour,
  DurationValue.SeventyTwoHour,
  DurationValue.SevenDay,
  DurationValue.TenDay,
];

export const getDurationOption = (
  t: TFunction,
  value: DurationValue,
): DurationOption => {
  const optionMap = {
    [DurationValue.OneHour]: {
      label: t('datePicker:pastHour'),
      value: DurationValue.OneHour,
      zendeskValue: 'pastHour',
    },
    [DurationValue.TwelveHour]: {
      label: t('datePicker:past12'),
      value: DurationValue.TwelveHour,
      zendeskValue: 'past12Hours',
    },
    [DurationValue.TwentyFourHour]: {
      label: t('datePicker:past24'),
      value: DurationValue.TwentyFourHour,
      zendeskValue: 'past24Hours',
    },
    [DurationValue.SeventyTwoHour]: {
      label: t('datePicker:past72'),
      value: DurationValue.SeventyTwoHour,
      zendeskValue: 'past72Hours',
    },
    [DurationValue.SevenDay]: {
      label: t('datePicker:past7Days'),
      value: DurationValue.SevenDay,
      zendeskValue: 'past7Days',
    },
    [DurationValue.TenDay]: {
      label: t('datePicker:past10Days'),
      value: DurationValue.TenDay,
      zendeskValue: 'past10Days',
    },
  };

  return optionMap[value];
};

export const getDurationList = (
  t: TFunction,
  durationValueFilter?: DurationValue[] | undefined,
): DurationOption[] => {
  return (durationValueFilter || ALL_DURATION_OPTIONS).map((value) =>
    getDurationOption(t, value),
  );
};

const REG_EX_DURATION = /^([\d]+)([\D]+)$/;

/** Calculates what startDate by subtracting the given duration from the given endDate
 *  for example, when duration is "7d", subtracts 7 days from endDate to determine startDate
 */
export const getStartDate = (
  duration: string,
  endDate: dayjs.Dayjs,
): dayjs.Dayjs => {
  const matchArr = duration.match(REG_EX_DURATION);
  const value = (matchArr && Number(matchArr[1])) as number;
  const unit = (matchArr && matchArr[2]) as ManipulateType;
  return dayjs(endDate).subtract(value, unit);
};

/** Calculates the startDate from the given duration and endTime, and converts to UTC and formats it */
export const getDurationStartTime = (
  duration: string,
  endTime: string,
): string => {
  return getStartDate(duration, dayjs(endTime)).utc().format();
};

/** Returns the current time in UTC */
export const getDurationEndTime = () => dayjs().utc().format();

export const getSelectedDuration = (
  initDuration: string | undefined | null,
  durationList: DurationOption[],
  defaultDuration: DurationOption,
): DurationOption => {
  const currentDuration = initDuration
    ? durationList.find(({ value }) => value === initDuration)
    : defaultDuration;

  return currentDuration || defaultDuration;
};

export const getDurationValueBasedOnStartEnd = (
  startTime: string,
  endTime: string,
): DurationValue | '' => {
  const diffHour = dayjs(endTime).diff(dayjs(startTime), 'hour');
  const diffDay = dayjs(endTime).diff(dayjs(startTime), 'day');

  if (diffHour === 1) {
    return DurationValue.OneHour;
  }
  if (diffHour === 12) {
    return DurationValue.TwelveHour;
  }
  if (diffHour === 24) {
    return DurationValue.TwentyFourHour;
  }
  if (diffHour === 72) {
    return DurationValue.SeventyTwoHour;
  }
  if (diffDay === 7) {
    return DurationValue.SevenDay;
  }
  if (diffDay === 10) {
    return DurationValue.TenDay;
  }

  return '';
};

export const getDefaultDuration = (
  storageKey?: DurationType['storageKey'],
): DurationValue => {
  if (storageKey) {
    return (window.localStorage.getItem(storageKey) ??
      DEFAULT_DURATION) as DurationValue;
  }
  return DEFAULT_DURATION;
};

export const setDurationInLocalStorage = (key: string, value: string): void =>
  window.localStorage.setItem(key, value);

export const getStartEndDateToPassToDetailsPage = (
  storageKey?: DurationType['storageKey'],
): { startTime: string; endTime: string } => {
  const newDuration = getDefaultDuration(storageKey);
  const endTime = dayjs().utc().format();
  const startTime = getDurationStartTime(newDuration, endTime);
  return { endTime, startTime };
};

export const getUrlWithDateParams = (
  url: string,
  dateParams: { start?: string | null; end?: string | null },
): string => {
  const { startTime, endTime } = getStartEndDateToPassToDetailsPage();

  const dataParam = `start=${encodeURIComponent(
    dateParams.start ?? startTime,
  )}&end=${encodeURIComponent(dateParams.end ?? endTime)}`;

  return url.includes('?') ? `${url}&${dataParam}` : `${url}?${dataParam}`;
};
